/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import EmojiReactions from '../components/EmojiReactions';
import Header from '../components/Header';
import ParticipantList from '../components/ParticipantList';
import RoundControls from '../components/RoundControls';
import VotingCards from '../components/VotingCards';
import VotingResults from '../components/VotingResults';
import { db } from '../firebase/firebase';
import { signInAnonymously } from '../utils/authUtils';
import {
    startNewRound,
    submitVote,
    revealVotes,
    updateUserActivity,
    updateCardDeck,
} from '../utils/roomUtils';
import { Room, Round, CardDeckType } from '../utils/types';
import '../styles/main.css';

interface RoomParams {
    roomCode: string;
}

const TShirtSizeReference: React.FC = () => {
    const tshirtSizeMapping: {
        [key: string]: {
            weeks: string;
            sprints: string;
        };
    } = {
        XXS: { weeks: '0 - 2 weeks', sprints: 'Up to 1 Sprint' },
        XS: { weeks: '2 - 4 weeks', sprints: 'Up to 2 Sprints' },
        S: { weeks: '4 - 6 weeks', sprints: 'Up to 3 Sprints' },
        M: { weeks: '6 - 10 weeks', sprints: 'Up to 5 Sprints' },
        L: { weeks: '10 - 16 weeks', sprints: 'Up to 8 Sprints' },
        XL: {
            weeks: '16+ weeks',
            sprints: '8 Sprints or more',
        },
        '?': { weeks: 'Not sure', sprints: 'Not sure' },
    };

    return (
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-md p-4">
            <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
                T-Shirt Size Reference
            </h4>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-600">
                            <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300 w-16">
                                Size
                            </th>
                            <th className="text-left py-2 px-2 font-medium text-gray-700 dark:text-gray-300 w-24">
                                Duration
                            </th>
                            <th className="text-left py-2 pl-2 font-medium text-gray-700 dark:text-gray-300">
                                Sprints
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(tshirtSizeMapping).map(
                            ([size, info]) => (
                                <tr
                                    key={size}
                                    className="border-b border-gray-100 dark:border-gray-700"
                                >
                                    <td className="py-2 pr-4 font-medium text-gray-900 dark:text-gray-100 w-16">
                                        {size}
                                    </td>
                                    <td className="py-2 px-2 text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                                        {info.weeks}
                                    </td>
                                    <td className="py-2 pl-2 text-gray-600 dark:text-gray-400">
                                        {info.sprints}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const RoomPage: React.FC = () => {
    const { roomCode } = useParams<RoomParams>();
    const history = useHistory();

    const [room, setRoom] = useState<Room | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userId, setUserId] = useState<string | null>(null);
    const [currentRound, setCurrentRound] = useState<Round | null>(null);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [userName, setUserName] = useState<string>('');
    const [roundTransition, setRoundTransition] = useState(false);
    const [autoRevealCountdown, setAutoRevealCountdown] = useState<
        number | null
    >(null);
    const autoRevealTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const setupRoom = async () => {
            try {
                // Ensure user is authenticated anonymously
                const authenticatedUserId = await signInAnonymously();
                setUserId(authenticatedUserId);

                // Get username from localStorage if available
                const storedUserName = localStorage.getItem(
                    `pp_${roomCode}_userName`,
                );
                if (storedUserName) {
                    setUserName(storedUserName);
                }

                const roomRef = db.ref(`rooms/${roomCode}`);
                console.log('Setting up room connection for room:', roomCode);

                // Test if we can read the room first
                try {
                    const testSnapshot = await roomRef.once('value');
                    if (!testSnapshot.exists()) {
                        setError('Room not found');
                        setLoading(false);
                        return;
                    }
                } catch (testError) {
                    console.error('Permission test failed:', testError);
                    setError(
                        'Unable to access room. Please try refreshing the page.',
                    );
                    setLoading(false);
                    return;
                }

                // Check if user is already a participant, if not, redirect to join flow
                const participantSnapshot = await roomRef
                    .child(`participants/${authenticatedUserId}`)
                    .once('value');
                if (!participantSnapshot.exists()) {
                    // User is not a participant, redirect to home with room code
                    history.push(`/?join=${roomCode}`);
                    return;
                }

                // Initial user activity update - do this just once
                if (authenticatedUserId) {
                    try {
                        await updateUserActivity(roomCode, authenticatedUserId);
                        console.log('Initial activity status updated');
                    } catch (err) {
                        console.error(
                            'Failed to update initial user activity:',
                            err,
                        );
                        // Continue even if this fails
                    }
                }

                roomRef.on(
                    'value',
                    (snapshot) => {
                        if (snapshot.exists()) {
                            const roomData = snapshot.val() as Room;

                            // Ensure cardDeck exists (for backward compatibility)
                            if (!roomData.cardDeck) {
                                roomData.cardDeck = {
                                    type: 'fibonacci',
                                    values: [
                                        '0',
                                        '1',
                                        '2',
                                        '3',
                                        '5',
                                        '8',
                                        '13',
                                        '21',
                                        '?',
                                    ],
                                };
                                db.ref(`rooms/${roomCode}/cardDeck`).set(
                                    roomData.cardDeck,
                                );
                            }

                            setRoom(roomData);

                            // Track round transitions
                            if (
                                currentRound === null ||
                                (roomData.currentRound &&
                                    roomData.currentRound !== currentRound?.id)
                            ) {
                                setRoundTransition(true);
                                setTimeout(
                                    () => setRoundTransition(false),
                                    500,
                                );
                            }

                            // Update current round if available
                            if (
                                roomData.currentRound &&
                                roomData.rounds[roomData.currentRound]
                            ) {
                                const currentRoundData =
                                    roomData.rounds[roomData.currentRound];
                                setCurrentRound(currentRoundData);

                                // Check if user has already voted in this round
                                if (
                                    currentRoundData.votes &&
                                    currentRoundData.votes[authenticatedUserId]
                                ) {
                                    setSelectedValue(
                                        currentRoundData.votes[
                                            authenticatedUserId
                                        ].value,
                                    );
                                } else {
                                    setSelectedValue(null);
                                }

                                // Check if all active participants have voted
                                // Check if all active participants have voted
                                if (
                                    !currentRoundData.revealed &&
                                    currentRoundData.votes
                                ) {
                                    const activeParticipants = Object.values(
                                        roomData.participants,
                                    ).filter((p) => p.isActive);
                                    const voteCount = Object.keys(
                                        currentRoundData.votes,
                                    ).length;

                                    if (
                                        activeParticipants.length > 0 &&
                                        voteCount === activeParticipants.length
                                    ) {
                                        // All active participants have voted
                                        if (!autoRevealTimerRef.current) {
                                            // Show countdown for all users
                                            console.log(
                                                'All participants voted, showing countdown',
                                            );

                                            // Start countdown
                                            setAutoRevealCountdown(3);

                                            // Set up countdown interval
                                            let countdown = 3;
                                            countdownIntervalRef.current =
                                                setInterval(() => {
                                                    countdown -= 1;
                                                    setAutoRevealCountdown(
                                                        countdown,
                                                    );

                                                    if (countdown <= 0) {
                                                        if (
                                                            countdownIntervalRef.current
                                                        ) {
                                                            clearInterval(
                                                                countdownIntervalRef.current,
                                                            );
                                                            countdownIntervalRef.current =
                                                                null;
                                                        }
                                                    }
                                                }, 1000);

                                            // Only admin initiates the auto-reveal
                                            if (
                                                roomData.participants[
                                                    authenticatedUserId
                                                ]?.isAdmin
                                            ) {
                                                autoRevealTimerRef.current =
                                                    setTimeout(() => {
                                                        revealVotes(
                                                            roomCode,
                                                            currentRoundData.id,
                                                        ).catch((err) => {
                                                            console.error(
                                                                'Failed to auto-reveal votes:',
                                                                err,
                                                            );
                                                        });
                                                        autoRevealTimerRef.current =
                                                            null;
                                                        setAutoRevealCountdown(
                                                            null,
                                                        );
                                                    }, 3000);
                                            } else {
                                                // Non-admin users just clear countdown after 3 seconds
                                                autoRevealTimerRef.current =
                                                    setTimeout(() => {
                                                        autoRevealTimerRef.current =
                                                            null;
                                                        setAutoRevealCountdown(
                                                            null,
                                                        );
                                                    }, 3000);
                                            }
                                        }
                                    } else {
                                        // Not all have voted - clear timers if they exist
                                        if (autoRevealTimerRef.current) {
                                            clearTimeout(
                                                autoRevealTimerRef.current,
                                            );
                                            autoRevealTimerRef.current = null;
                                        }
                                        if (countdownIntervalRef.current) {
                                            clearInterval(
                                                countdownIntervalRef.current,
                                            );
                                            countdownIntervalRef.current = null;
                                        }
                                        setAutoRevealCountdown(null);
                                    }
                                } else {
                                    // Round is revealed or no votes yet - clear timers if they exist
                                    if (autoRevealTimerRef.current) {
                                        clearTimeout(
                                            autoRevealTimerRef.current,
                                        );
                                        autoRevealTimerRef.current = null;
                                    }
                                    if (countdownIntervalRef.current) {
                                        clearInterval(
                                            countdownIntervalRef.current,
                                        );
                                        countdownIntervalRef.current = null;
                                    }
                                    setAutoRevealCountdown(null);
                                }
                            } else {
                                setCurrentRound(null);
                                setSelectedValue(null);
                                // Clear timers if they exist
                                if (autoRevealTimerRef.current) {
                                    clearTimeout(autoRevealTimerRef.current);
                                    autoRevealTimerRef.current = null;
                                }
                                if (countdownIntervalRef.current) {
                                    clearInterval(countdownIntervalRef.current);
                                    countdownIntervalRef.current = null;
                                }
                                setAutoRevealCountdown(null);
                            }

                            setLoading(false);
                        } else {
                            setError('Room not found');
                            setLoading(false);
                        }
                    },
                    (err) => {
                        setError('Failed to load room data');
                        setLoading(false);
                        console.error(err);
                    },
                );

                let lastUpdateTime = Date.now();
                const MINIMUM_UPDATE_INTERVAL = 20000;

                const activityInterval = setInterval(() => {
                    const now = Date.now();
                    if (
                        authenticatedUserId &&
                        now - lastUpdateTime >= MINIMUM_UPDATE_INTERVAL
                    ) {
                        lastUpdateTime = now;
                        updateUserActivity(roomCode, authenticatedUserId).catch(
                            (err) => {
                                console.error(
                                    'Periodic activity update failed:',
                                    err,
                                );
                            },
                        );
                    }
                }, 30000);

                return async () => {
                    console.log('Cleaning up room listeners');
                    roomRef.off();
                    clearInterval(activityInterval);
                    if (autoRevealTimerRef.current) {
                        clearTimeout(autoRevealTimerRef.current);
                    }
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                    }

                    // Cancel the onDisconnect handlers and set user as inactive
                    if (authenticatedUserId) {
                        const userStatusRef = db.ref(
                            `/rooms/${roomCode}/participants/${authenticatedUserId}`,
                        );
                        await userStatusRef.onDisconnect().cancel();
                        await userStatusRef.update({
                            isActive: false,
                            lastSeen: Date.now(),
                        });
                    }
                };
            } catch (error) {
                console.error('Error setting up room:', error);
                setError('Failed to set up room');
                setLoading(false);
            }
        };

        setupRoom();
    }, [roomCode, history]);

    const handleVote = async (value: string) => {
        if (!userId || !currentRound) {
            return;
        }

        try {
            if (value === '') {
                // Remove the vote
                await db
                    .ref(
                        `rooms/${roomCode}/rounds/${currentRound.id}/votes/${userId}`,
                    )
                    .remove();
                setSelectedValue(null);
            } else {
                await submitVote(roomCode, currentRound.id, userId, value);
                setSelectedValue(value);
            }
        } catch (err) {
            console.error('Failed to submit vote:', err);
        }
    };

    const handleStartNewRound = async (description: string) => {
        try {
            await startNewRound(roomCode, description);
        } catch (err) {
            console.error('Failed to start new round:', err);
        }
    };

    const handleRevealVotes = async () => {
        if (!currentRound) {
            return;
        }

        try {
            await revealVotes(roomCode, currentRound.id);
        } catch (err) {
            console.error('Failed to reveal votes:', err);
        }
    };

    const handleUpdateCardDeck = async (
        deckType: CardDeckType,
        customValues?: string[],
    ) => {
        try {
            await updateCardDeck(roomCode, deckType, customValues);
        } catch (err) {
            console.error('Failed to update card deck:', err);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
                <Header />
                <main className="flex items-center justify-center h-screen">
                    <div className="text-center p-8 bg-white dark:bg-dark-card rounded-lg shadow-lg max-w-md mx-auto">
                        <div className="animate-spin mx-auto mb-4 h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
                        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                            Loading room...
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2">
                            Setting up your planning session
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    if (error || !room) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
                <Header />
                <main className="flex items-center justify-center h-screen">
                    <div className="text-center p-8 bg-white dark:bg-dark-card rounded-lg shadow-lg max-w-md mx-auto">
                        <div className="mx-auto mb-4 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-red-600 dark:text-red-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">
                            {error || 'Room not found'}
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2 mb-4">
                            The room you're looking for doesn't exist or is no
                            longer available.
                        </p>
                        <button
                            onClick={() => history.push('/')}
                            className="inline-flex items-center justify-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md transition-colors duration-200"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                />
                            </svg>
                            Back to Home
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    const isAdmin = userId
        ? room.participants[userId]?.isAdmin || false
        : false;

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 pb-12">
            <Header roomName={room.name} roomCode={room.id} />

            {userId && <EmojiReactions roomCode={roomCode} userId={userId} />}

            <main className="max-w-7xl mx-auto px-4 py-8">
                <div
                    className={`
                    p-6 mb-6 rounded-lg shadow-md relative overflow-hidden
                    transition-all duration-300 transform
                    ${roundTransition ? 'scale-102 translate-y-2' : ''}
                    ${
                        currentRound
                            ? currentRound.revealed
                                ? 'bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-100 dark:border-purple-800'
                                : autoRevealCountdown !== null
                                  ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-100 dark:border-yellow-800'
                                  : 'bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 border border-green-100 dark:border-green-800'
                            : 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-100 dark:border-amber-800'
                    }
                `}
                >
                    {currentRound ? (
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                            <div>
                                <div className="flex items-center">
                                    {currentRound.revealed ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6 mr-2 text-purple-500"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    ) : autoRevealCountdown !== null ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6 mr-2 text-yellow-500 animate-spin"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6 mr-2 text-green-500"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    )}
                                    <h2 className="text-xl font-bold dark:text-dark-text">
                                        {currentRound.revealed
                                            ? 'Voting Complete'
                                            : autoRevealCountdown !== null
                                              ? 'All Votes Are In!'
                                              : 'Active Round'}
                                    </h2>
                                </div>

                                {currentRound.description && (
                                    <p className="mt-2 text-gray-700 dark:text-gray-300 font-medium">
                                        {currentRound.description}
                                    </p>
                                )}

                                {autoRevealCountdown !== null && (
                                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                                        Results will be revealed
                                        automatically...
                                    </p>
                                )}
                            </div>

                            <div className="mt-4 md:mt-0">
                                {currentRound.revealed ? (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                        Results Revealed
                                    </span>
                                ) : autoRevealCountdown !== null ? (
                                    <div className="animate-pulse">
                                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 mr-2 animate-spin"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            Revealing in {autoRevealCountdown}
                                            ...
                                        </span>
                                    </div>
                                ) : (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                        Voting in Progress
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-12 w-12 mx-auto mb-3 text-amber-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h2 className="text-xl font-bold mb-2 dark:text-dark-text">
                                Waiting for a new round to start
                            </h2>
                            {isAdmin && (
                                <p className="text-gray-600 dark:text-gray-400">
                                    As the admin, you can start a new round
                                    using the controls below.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Background decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-10 transform rotate-45">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5"
                            />
                        </svg>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-1">
                        <ParticipantList
                            participants={room.participants}
                            currentVotes={currentRound?.votes}
                            revealed={currentRound?.revealed || false}
                        />

                        {room.cardDeck.type === 'tshirt' && (
                            <div className="mt-6">
                                <TShirtSizeReference />
                            </div>
                        )}
                    </div>

                    <div className="xl:col-span-2">
                        {currentRound && !currentRound.revealed && (
                            <div className="bg-white dark:bg-dark-card rounded-lg shadow-md p-6 mb-6">
                                <VotingCards
                                    onVote={handleVote}
                                    selectedValue={selectedValue}
                                    disabled={false}
                                    cardValues={room.cardDeck.values}
                                />
                            </div>
                        )}

                        {currentRound && currentRound.revealed && (
                            <VotingResults
                                votes={currentRound.votes}
                                revealed={currentRound.revealed}
                            />
                        )}

                        {isAdmin && (
                            <RoundControls
                                onStartNewRound={handleStartNewRound}
                                onRevealVotes={handleRevealVotes}
                                onUpdateCardDeck={handleUpdateCardDeck}
                                isAdmin={isAdmin}
                                revealed={currentRound?.revealed || false}
                                currentDeckType={room.cardDeck.type}
                                currentCardValues={room.cardDeck.values}
                            />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default RoomPage;
